import { EyeOff, History } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import type { ModpackConfig } from '../../types/modpackConfig';
import { isHidden, lastWipe } from './configFields';
import { cn } from '../../utils/cn';

/**
 * The two things about a config that are worth a badge.
 *
 * "Wiped" is not a status — a pack wiped a year ago is just a pack whose world
 * is a year old — so the wipe is shown as a date, the way players are told it.
 * Hidden is a state: the pack has been withdrawn from everything public.
 */

export const HiddenBadge = ({ config, className }: { config: Pick<ModpackConfig, 'hidden'>; className?: string }) => {
  if (!isHidden(config)) return null;
  return (
    <span
      className={cn('badge badge-warning inline-flex items-center gap-1', className)}
      title="Withdrawn from the public API and the LFG list. Staff and Mr. Roboto still see it."
    >
      <EyeOff className="w-3 h-3" />
      Hidden
    </span>
  );
};

export const formatWipe = (seconds: number) =>
  new Date(seconds * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export const wipeAgo = (seconds: number) =>
  formatDistanceToNowStrict(new Date(seconds * 1000), { addSuffix: true });

/**
 * When the world was last wiped, relative, with the exact time on hover.
 * Nothing at all for a pack that has never been wiped unless `showNever`.
 */
export const LastWipe = ({ config, className, showNever = false, prefix = 'Wiped' }: {
  config: Pick<ModpackConfig, 'date_wiped'>;
  className?: string;
  showNever?: boolean;
  prefix?: string;
}) => {
  const wiped = lastWipe(config);
  if (wiped === null) {
    return showNever ? <span className={cn('text-xs text-glass-500', className)}>Never wiped</span> : null;
  }
  return (
    <span
      className={cn('inline-flex items-center gap-1 text-xs text-glass-300 tabular-nums whitespace-nowrap', className)}
      title={`Last world wipe: ${formatWipe(wiped)}`}
    >
      <History className="w-3 h-3 text-glass-400" />
      {prefix ? `${prefix} ` : ''}{wipeAgo(wiped)}
    </span>
  );
};
